import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import PhoneInput from '../components/PhoneInput';
import PasswordInput from '../components/PasswordInput';
import './Auth.css';
import './Auth.simplified.css';

/**
 * Auth page — combined sign-in and sign-up.
 *
 * Design priorities for an Indian e-commerce customer:
 *   1. The two methods most familiar to Indians are surfaced as big,
 *      one-click options at the top: Google and Phone OTP.
 *   2. Email + password is collapsed into a "Sign in with email" expander
 *      below; admins and returning customers can still use it.
 *   3. New users get a single in-page toggle ("New here? Create account")
 *      that flips the email form to a registration form on the same page —
 *      no separate /register navigation.
 *   4. Role selection (Customer / Electrician / Engineer / Dealer) is
 *      hidden behind an "I'm a professional" checkbox; 99% of shoppers
 *      never see it. Customer is the implicit default.
 *
 * URL query string `?mode=signup` opens the page in registration mode
 * (used by /register redirect for old links).
 */

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isAuthenticated,
    login,
    register,
    loginWithGoogle,
    startPhoneSignIn,
    confirmPhoneCode,
    sendPasswordReset,
    awaitingEmailVerification,
    resendVerificationEmail,
    retryAfterVerification,
    cancelEmailVerification,
  } = useAuth();

  // Page mode + UI state
  const initialMode = searchParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const [mode, setMode] = useState(initialMode);
  const [emailFormOpen, setEmailFormOpen] = useState(initialMode === 'signup'); // signup auto-expands the form
  const [phoneFlowOpen, setPhoneFlowOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showProfessional, setShowProfessional] = useState(false);

  // Form state
  const [emailForm, setEmailForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'customer',
    licenseNumber: '',
    firmName: '',
    gstNumber: '',
    termsAccepted: false,
  });
  const [phoneForm, setPhoneForm] = useState({ phone: '', code: '' });

  const recaptchaId = 'auth-recaptcha-container';

  // Redirect once the user is actually authenticated. This is driven by
  // onAuthStateChanged (via AuthContext), NOT by the sign-in call's return
  // value. Browsers with a strict Cross-Origin-Opener-Policy can leave the
  // signInWithPopup() promise hanging forever after the Google popup closes —
  // the sign-in succeeds but the await never resolves, so the old
  // navigate('/') after `await loginWithGoogle()` never ran and the page sat
  // greyed-out (every button disabled) until a manual refresh. Watching the
  // auth state instead makes navigation independent of that quirk.
  useEffect(() => {
    if (isAuthenticated && !awaitingEmailVerification) {
      navigate(location.state?.from?.pathname || '/', { replace: true });
    }
  }, [isAuthenticated, awaitingEmailVerification, navigate, location.state]);

  const updateEmailForm = (patch) => setEmailForm((s) => ({ ...s, ...patch }));

  // ---------- Google -------------------------------------------------------
  const handleGoogle = async () => {
    try {
      setLoading(true);
      await loginWithGoogle();
      toast.success('Signed in with Google');
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Phone OTP ----------------------------------------------------
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phoneForm.phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    try {
      setLoading(true);
      const conf = await startPhoneSignIn(`+91${phoneForm.phone}`, recaptchaId);
      setConfirmation(conf);
      toast.success('OTP sent to your mobile');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!confirmation || phoneForm.code.length < 4) {
      toast.error('Enter the 6-digit code from your SMS');
      return;
    }
    try {
      setLoading(true);
      await confirmPhoneCode(confirmation, phoneForm.code);
      toast.success('Signed in');
      navigate('/');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Email/password sign-in ---------------------------------------
  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!emailForm.email || !emailForm.password) {
      toast.error('Enter your email and password');
      return;
    }
    try {
      setLoading(true);
      await login(emailForm.email, emailForm.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Sign-up ------------------------------------------------------
  const validateSignup = () => {
    if (!emailForm.name || !emailForm.email || !emailForm.password) {
      toast.error('Please fill in name, email, and password');
      return false;
    }
    if (emailForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (emailForm.phone && !/^[6-9]\d{9}$/.test(emailForm.phone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return false;
    }
    if (!emailForm.termsAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return false;
    }
    if (showProfessional) {
      if (emailForm.role === 'electrician' && !emailForm.licenseNumber) {
        toast.error('Please enter your license number');
        return false;
      }
      if (emailForm.role === 'engineer' && !emailForm.firmName) {
        toast.error('Please enter your firm name');
        return false;
      }
      if (emailForm.role === 'dealer' && !emailForm.gstNumber) {
        toast.error('Please enter your GST number');
        return false;
      }
    }
    return true;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;
    try {
      setLoading(true);
      const role = showProfessional ? emailForm.role : 'customer';
      await register({
        name: emailForm.name,
        email: emailForm.email,
        password: emailForm.password,
        phone: emailForm.phone || undefined,
        role,
        consent: { termsAccepted: true, privacyAccepted: true, marketingOptIn: false },
        ...(role === 'electrician' && { profile: { licenseNumber: emailForm.licenseNumber } }),
        ...(role === 'engineer' && { profile: { firmName: emailForm.firmName } }),
        ...(role === 'dealer' && { profile: { gstNumber: emailForm.gstNumber } }),
      });
      // Don't navigate — onAuthStateChanged → syncProfile will fail with
      // 403 EMAIL_VERIFICATION_REQUIRED and AuthContext flips to the
      // awaitingEmailVerification state. The "Check your email" view below
      // takes over rendering. Clear the password from memory either way.
      updateEmailForm({ password: '' });
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  // ---------- Verification-pending actions --------------------------------
  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail();
      toast.success('Verification email sent again. Check your inbox (and spam).');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifiedContinue = async () => {
    try {
      setLoading(true);
      const profile = await retryAfterVerification();
      if (profile) {
        toast.success('Email verified — welcome!');
        navigate('/');
      } else {
        toast(
          "We still don't see your email as verified. Click the link in your inbox, then try again.",
          { icon: '⏳', duration: 6000 }
        );
      }
    } catch (err) {
      toast.error(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelVerification = async () => {
    await cancelEmailVerification();
    toast('Signed out. You can sign in once you verify your email.', { duration: 4000 });
  };

  // ---------- Forgot password ----------------------------------------------
  const handleForgotPassword = async () => {
    const email = (emailForm.email || prompt('Enter your account email:') || '').trim();
    if (!email) return;
    try {
      await sendPasswordReset(email);
      toast.success('Password reset link sent. Check your inbox.');
    } catch (err) {
      toast.error(friendlyAuthError(err));
    }
  };

  // ---------- Mode toggle helpers ------------------------------------------
  const switchTo = (next) => {
    setMode(next);
    setEmailFormOpen(true);
    setPhoneFlowOpen(false);
    setConfirmation(null);
  };

  // ---------- Render -------------------------------------------------------
  const headerTitle = mode === 'signup' ? 'Create Account' : 'Sign In';
  const headerCopy = mode === 'signup'
    ? 'Join Ceedees Electricals — takes a minute.'
    : 'Welcome back to Ceedees Electricals.';

  return (
    <>
      <Helmet>
        <title>{mode === 'signup' ? 'Create Account' : 'Sign In'} - Ceedees Electricals</title>
      </Helmet>

      <div className="auth-page">
        <div className="auth-container">
          {/* Verification-pending screen takes over the whole card. We
              deliberately replace (not add to) the normal auth UI here so
              the user can't accidentally start a second sign-up while a
              previous one is half-done. */}
          {awaitingEmailVerification ? (
            <div className="auth-card">
              <div className="auth-header">
                <h1>Check your email</h1>
                <p>One last step to keep your account safe.</p>
              </div>
              <div className="auth-verify-card">
                <div className="auth-verify-card__icon">📧</div>
                <p>
                  We sent a verification link to{' '}
                  <strong>{awaitingEmailVerification.email || 'your inbox'}</strong>.
                  Click the link, then come back here and continue.
                </p>
                <p className="auth-verify-card__hint">
                  Don't see it? Check your spam folder. The email comes from{' '}
                  <code>noreply@ceedees-electricals.firebaseapp.com</code>.
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-lg auth-submit"
                  onClick={handleVerifiedContinue}
                  disabled={loading}
                >
                  {loading ? 'Checking...' : "I've verified — continue"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline auth-submit"
                  onClick={handleResendVerification}
                  disabled={loading}
                >
                  Resend verification email
                </button>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={handleCancelVerification}
                  disabled={loading}
                >
                  Use a different account
                </button>
              </div>
            </div>
          ) : (
          <div className="auth-card">
            <div className="auth-header">
              <h1>{headerTitle}</h1>
              <p>{headerCopy}</p>
            </div>

            {/* Primary buttons — always visible, fastest path for most users */}
            <button
              type="button"
              className="btn btn-primary btn-lg auth-primary"
              onClick={handleGoogle}
              disabled={loading}
            >
              <span className="auth-primary__icon">G</span>
              Continue with Google
            </button>

            {!phoneFlowOpen && (
              <button
                type="button"
                className="btn btn-outline btn-lg auth-primary"
                onClick={() => setPhoneFlowOpen(true)}
                disabled={loading}
              >
                <span className="auth-primary__icon">📱</span>
                Continue with phone OTP
              </button>
            )}

            {phoneFlowOpen && !confirmation && (
              <form onSubmit={handleSendOtp} className="auth-inline-form">
                <label>Mobile number</label>
                <PhoneInput
                  value={phoneForm.phone}
                  onChange={(v) => setPhoneForm({ ...phoneForm, phone: v })}
                  required
                  autoFocus
                />
                <div className="auth-inline-form__actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Sending...' : 'Send OTP'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => { setPhoneFlowOpen(false); setPhoneForm({ phone: '', code: '' }); }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {phoneFlowOpen && confirmation && (
              <form onSubmit={handleVerifyOtp} className="auth-inline-form">
                <label>Enter the 6-digit code we just sent to +91 {phoneForm.phone}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={phoneForm.code}
                  onChange={(e) => setPhoneForm({ ...phoneForm, code: e.target.value.replace(/\D/g, '') })}
                  placeholder="123456"
                  required
                  autoFocus
                />
                <div className="auth-inline-form__actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify & Sign in'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => { setConfirmation(null); setPhoneForm({ ...phoneForm, code: '' }); }}
                  >
                    Use a different number
                  </button>
                </div>
              </form>
            )}

            {/* Invisible reCAPTCHA mount point — must exist whenever phone flow runs */}
            <div id={recaptchaId} />

            {/* Implicit consent notice for OAuth/phone */}
            <p className="auth-fineprint">
              By continuing with Google or phone OTP, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>

            <div className="divider"><span>or</span></div>

            {/* Email/password section — collapsed by default for sign-in,
                expanded for sign-up. The mode-toggle line below this block
                ("New here? Create an account") is always visible so users
                don't have to expand the email form to discover signup. */}
            {!emailFormOpen ? (
              <button
                type="button"
                className="btn btn-link auth-expander"
                onClick={() => setEmailFormOpen(true)}
              >
                Sign in with email and password →
              </button>
            ) : mode === 'signin' ? (
              <form onSubmit={handleSignIn} className="auth-form">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={emailForm.email}
                    onChange={(e) => updateEmailForm({ email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <PasswordInput
                    autoComplete="current-password"
                    value={emailForm.password}
                    onChange={(e) => updateEmailForm({ password: e.target.value })}
                    required
                  />
                </div>
                <div className="form-footer">
                  <span />
                  <button type="button" className="btn btn-link" onClick={handleForgotPassword}>
                    Forgot password?
                  </button>
                </div>
                <button type="submit" className="btn btn-primary btn-lg auth-submit" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignUp} className="auth-form">
                <div className="form-group">
                  <label>Full name</label>
                  <input
                    type="text"
                    autoComplete="name"
                    value={emailForm.name}
                    onChange={(e) => updateEmailForm({ name: e.target.value })}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={emailForm.email}
                    onChange={(e) => updateEmailForm({ email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mobile number <span className="muted">(optional)</span></label>
                  <PhoneInput
                    value={emailForm.phone}
                    onChange={(v) => updateEmailForm({ phone: v })}
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <PasswordInput
                    autoComplete="new-password"
                    value={emailForm.password}
                    onChange={(e) => updateEmailForm({ password: e.target.value })}
                    placeholder="At least 8 characters"
                    minLength={8}
                    required
                  />
                </div>

                {/* Professional roles tucked away — most users never see this */}
                <label className="auth-pro-toggle">
                  <input
                    type="checkbox"
                    checked={showProfessional}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowProfessional(checked);
                      if (!checked) updateEmailForm({ role: 'customer' });
                    }}
                  />
                  <span>I'm a professional (electrician, engineer, dealer)</span>
                </label>

                {showProfessional && (
                  <>
                    <div className="role-tabs role-tabs--small">
                      {[
                        { value: 'electrician', label: 'Electrician' },
                        { value: 'engineer', label: 'Engineer' },
                        { value: 'dealer', label: 'Dealer' },
                      ].map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          className={`tab-btn ${emailForm.role === r.value ? 'active' : ''}`}
                          onClick={() => updateEmailForm({ role: r.value })}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                    {emailForm.role === 'electrician' && (
                      <div className="form-group">
                        <label>License number</label>
                        <input
                          type="text"
                          value={emailForm.licenseNumber}
                          onChange={(e) => updateEmailForm({ licenseNumber: e.target.value })}
                          placeholder="Your license number"
                          required
                        />
                      </div>
                    )}
                    {emailForm.role === 'engineer' && (
                      <div className="form-group">
                        <label>Firm name</label>
                        <input
                          type="text"
                          value={emailForm.firmName}
                          onChange={(e) => updateEmailForm({ firmName: e.target.value })}
                          placeholder="Your firm's name"
                          required
                        />
                      </div>
                    )}
                    {emailForm.role === 'dealer' && (
                      <div className="form-group">
                        <label>GST number</label>
                        <input
                          type="text"
                          value={emailForm.gstNumber}
                          onChange={(e) => updateEmailForm({ gstNumber: e.target.value })}
                          placeholder="32ABCDE1234F1Z5"
                          required
                        />
                      </div>
                    )}
                  </>
                )}

                <label className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={emailForm.termsAccepted}
                    onChange={(e) => updateEmailForm({ termsAccepted: e.target.checked })}
                  />
                  <span>
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                  </span>
                </label>

                <button type="submit" className="btn btn-primary btn-lg auth-submit" disabled={loading}>
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
              </form>
            )}

            {/* Mode toggle — always visible on the main view, no matter
                whether the email form is collapsed or expanded. Lets a
                first-time visitor discover "Create an account" without
                having to click "Sign in with email" first. */}
            <p className="auth-mode-toggle">
              {mode === 'signin' ? (
                <>
                  New here?{' '}
                  <button type="button" className="btn btn-link" onClick={() => switchTo('signup')}>
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button type="button" className="btn btn-link" onClick={() => switchTo('signin')}>
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
          )}

          <div className="auth-illustration">
            <div className="illustration-box">
              <div className="icon">⚡</div>
              <h3>Welcome to Ceedees</h3>
              <p>Pick whichever way you prefer to sign in. You can always add more sign-in methods from your dashboard later.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** Translate a Firebase auth error into a sentence a real user can act on. */
function friendlyAuthError(err) {
  const code = err?.code || '';
  const apiMsg = err?.response?.data?.message;
  if (err?.response?.data?.code === 'EMAIL_VERIFICATION_REQUIRED') {
    return 'Please verify your email before signing in. Check your inbox.';
  }
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/invalid-email':
      return 'That email doesn\'t look right.';
    case 'auth/weak-password':
      return 'Password is too weak. Use at least 8 characters with a mix of letters and numbers.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a few minutes or reset your password.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/invalid-phone-number':
      return "That phone number doesn't look right. Use a 10-digit Indian mobile number.";
    case 'auth/invalid-verification-code':
      return 'That code is incorrect. Please try again.';
    case 'auth/code-expired':
      return 'The OTP expired. Please request a new one.';
    case 'auth/captcha-check-failed':
      return 'Bot check failed. Please refresh the page and try again.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in popup. Allow popups for this site.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is already linked to a different sign-in method.';
    default:
      if (apiMsg && apiMsg.length < 200) return apiMsg;
      return err?.message || 'Sign-in failed. Please try again.';
  }
}

export default Login;
